import React, { useState, useEffect, ChangeEvent } from 'react';
import {
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Typography,
  Tooltip,
  Grid,
  Box,
  Stack,
} from '@mui/material';
import dayjs from 'dayjs';
import InfoIcon from '@mui/icons-material/Info';
import { useTranslation } from 'react-i18next';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { useContext } from 'react';
import { UserContext } from '../../../components/shared/useUser';
import { TransportCost } from '../../../app/services/api/generated';
import DatePickerLocalized from '../../../components/shared/DatePickerLocalized';
import useLogError from '../../../hooks/useLogError';

interface AddTransportCostProps {
    open: boolean;
    onClose: () => void;
    onCreated: () => void; // To refresh data in parent after creation
}

const AddTransportCost: React.FC<AddTransportCostProps> = ({ open, onClose, onCreated }) => {
  const { t } = useTranslation('common');
  const { createTransportCost, getCurrentUser } = useNswagClient();
  const { selectedStore } = useContext(UserContext);
  const { logError } = useLogError();

  const [startDate, setStartDate] = useState<string>(dayjs().startOf('week').format('YYYY-MM-DD'));
  
  const [expectedCost, setExpectedCost] = useState('');
  const [actualCost, setActualCost] = useState('');
  const [expectedCostError, setExpectedCostError] = useState('');
  const [actualCostError, setActualCostError] = useState('');
  const [userId, setUserId] = useState<number | undefined>();
  const [snackbar, setSnackbar] = useState<{
      open: boolean;
      message: string;
      severity: 'error' | 'warning' | 'info' | 'success';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const result = await getCurrentUser();
        if (result.data?.id) {
          setUserId(result.data.id);
        }
      } catch (error) {
        console.error('Error fetching current user:', error);
        localStorage.clear();
      }
    };

    fetchUser();
  }, [getCurrentUser]);

  useEffect(() => {
    if (open) {
      setStartDate(dayjs().startOf('week').format('YYYY-MM-DD'));

      setExpectedCost('');
      setActualCost('');
      setExpectedCostError('');
      setActualCostError('');
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!selectedStore?.storeNumber) return;

    const expected = parseFloat(expectedCost);

    if (!expectedCost || isNaN(expected) || expected < 0) {
      setExpectedCostError('Required');
      return;
    }

    let actual: number | undefined = undefined;
    if (actualCost) {
      const parsed = parseFloat(actualCost);
      if (isNaN(parsed) || parsed < 0) {
        setActualCostError('Value must be 0 or greater');
        return;
      }
      actual = parsed;
    }

    const payload: TransportCost = {
      storeNumber: selectedStore.storeNumber,
      startDate: startDate,
      expectedTransportCost: expected,
      actualTransportCost: actual,  // will be number or null
      createdById: userId,
      createdAt: new Date().toISOString(),
    };

    try {
      await createTransportCost(payload);
      onClose();
      onCreated();
    } catch (error) {
      logError(error);
      console.error('Failed to create transport cost:', error);
    }
  };

  const handleDeliveryDateChange = (date: string | null) : void => {
    if(date)
    {
      setStartDate(date);
    }
  };

  return (
    <Dialog open={open}
      onClose={onClose}
      fullWidth>
      <DialogTitle sx={{ pr: '48px' }}
        fontFamily={'SoDoSans'}
        fontSize={'18px'}
        fontWeight={600}>
        {t('transportCosts.addTransportCost')}
      </DialogTitle>
      <DialogContent>
        <FormControl fullWidth>
          <Grid item
            xl={12}
            md={12}
            xs={12}
            container
            direction="column"
            justifyContent="center"
            alignItems="center"
            sx={{ padding: 0 }} >
            <DatePickerLocalized
              label={t('transportCosts.startDate') + ' *'}
              useCalendar={false}
              value={startDate}
              onChange={handleDeliveryDateChange}
              shouldDisableDate={(date) => dayjs(date).day() !== 1 }
              disableHighlightToday={true}
              translations={{
                clearButtonLabel: t('calendar.clearButtonLabel'),
                todayButtonLabel: t('calendar.todayButtonLabel'),
                cancelButtonLabel: t('calendar.cancelButtonLabel'),
                okButtonLabel: t('calendar.okButtonLabel'),
              }}
            />
          </Grid>
        </FormControl>

        <FormControl fullWidth
          sx={{ mt: 5 }}>
          <TextField
            type="number"
            required={true}
            inputProps={{ min: 0 }}
            error={!!expectedCostError}
            helperText={expectedCostError}
            label={t('transportCosts.expected')}
            value={expectedCost}
            onChange={(e) => {
              const value = e.target.value;
              setExpectedCost(value);
              if (!value) {
                setExpectedCostError('Required');
              } else if (parseFloat(value) < 0) {
                setExpectedCostError('Value must be 0 or greater');
              } else {
                setExpectedCostError('');
              }
            }}
            fullWidth
          />
        </FormControl>

        <FormControl fullWidth
          sx={{ mt: 5 }}>
          <TextField
            type="number"
            inputProps={{ min: 0 }}
            error={!!actualCostError}
            helperText={actualCostError}
            label={t('transportCosts.actual')}
            value={actualCost}
            onChange={(e) => {
              const value = e.target.value;
              setActualCost(value);
              if (parseFloat(value) < 0) {
                setActualCostError('Value must be 0 or greater');
              } else {
                setActualCostError('');
              }
            }}
            fullWidth
          />
        </FormControl>
      </DialogContent>

      <DialogActions sx={{ px: '24px', mb: 4 }}>
        <Grid container
          spacing={4}
          justifyContent="space-between">
          
          <Grid item
            xs={12}>
            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              spacing={4}>
              <Button size="lg" 
                variant='secondary'
                onClick={onClose}>{t('transportCosts.cancel')}</Button>
              <Button size="lg" 
                variant='primary'
                onClick={handleSubmit}>
                {t('transportCosts.submit')}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
};

export default AddTransportCost;
